"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.VMConditionalCompiler = void 0;
const inversify_1 = require("inversify");
const IVMCompilerUtils_1 = require("../../../../interfaces/vm-transformers/IVMCompilerUtils");
const IVMScopeManager_1 = require("../../../../interfaces/vm-transformers/IVMScopeManager");
const ServiceIdentifiers_1 = require("../../../../container/ServiceIdentifiers");
const VMOpCode_1 = require("../../../../enums/vm-transformers/VMOpCode");
const NodeGuards_1 = require("../../../../node/NodeGuards");
let VMConditionalCompiler = class VMConditionalCompiler {
    compileIfStatement(node, context) {
        var _a, _b, _c;
        (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(node.test);
        const jumpToElseIndex = this.vmCompilerUtils.emitConditionalJump(VMOpCode_1.VMOpCode.JUMP_IF_FALSE, context);
        (_b = context.dispatch) === null || _b === void 0 ? void 0 : _b.statement(node.consequent);
        if (node.alternate) {
            const jumpToEndIndex = this.vmCompilerUtils.emitConditionalJump(VMOpCode_1.VMOpCode.JUMP, context);
            this.vmCompilerUtils.patchJump(jumpToElseIndex, context);
            (_c = context.dispatch) === null || _c === void 0 ? void 0 : _c.statement(node.alternate);
            this.vmCompilerUtils.patchJump(jumpToEndIndex, context);
        }
        else {
            this.vmCompilerUtils.patchJump(jumpToElseIndex, context);
        }
    }
    compileSwitchStatement(node, context) {
        var _a;
        (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(node.discriminant);
        const { localIndex: tempLocalIndex, actualIndex } = this.vmCompilerUtils.createTempLocal(`__switch_temp_${context.localVariables.size}`, context);
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        builder.storeLocal(actualIndex);
        const caseBodyTargets = [];
        const caseLabelTargets = [];
        const { defaultCaseIndex, defaultJumpIndex } = this.compileSwitchCaseComparisons(node.cases, tempLocalIndex, caseLabelTargets, context);
        const breakJumpIndex = this.compileSwitchCaseBodies(node.cases, caseBodyTargets, context);
        const endTarget = context.instructions.length;
        this.patchSwitchJumpTargets(caseLabelTargets, caseBodyTargets, defaultCaseIndex, defaultJumpIndex, breakJumpIndex, endTarget, context);
    }
    compileSwitchCaseComparisons(cases, tempLocalIndex, caseLabelTargets, context) {
        var _a;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        let defaultCaseIndex = -1;
        for (let i = 0; i < cases.length; i++) {
            const caseNode = cases[i];
            if (caseNode.test === null) {
                defaultCaseIndex = i;
                continue;
            }
            const actualTempIndex = this.vmCompilerUtils.getActualLocalIndex(tempLocalIndex, context);
            builder.loadLocal(actualTempIndex);
            (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(caseNode.test);
            builder.strictEqual();
            const jumpIfTrue = builder.jumpIfTrue();
            caseLabelTargets.push(jumpIfTrue.getIndex());
            jumpIfTrue.patchTo(-1);
        }
        const defaultJump = builder.jump();
        const defaultJumpIndex = defaultJump.getIndex();
        defaultJump.patchTo(-1);
        return { defaultCaseIndex, defaultJumpIndex };
    }
    compileSwitchCaseBodies(cases, caseBodyTargets, context) {
        var _a;
        const breakJumpIndex = context.instructions.length;
        context.breakTargets.push(breakJumpIndex);
        context.breakEnvDepths.push(context.envDepth);
        const switchTdzVars = this.scanSwitchCasesForLexicalDeclarations(cases);
        if (switchTdzVars.size > 0) {
            this.scopeManager.enterLocalsTDZ(context, [...switchTdzVars]);
        }
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        for (const varName of switchTdzVars) {
            if (context.envVars.has(varName)) {
                builder.declareTDZ(varName);
            }
        }
        for (let i = 0; i < cases.length; i++) {
            const caseNode = cases[i];
            caseBodyTargets[i] = context.instructions.length;
            for (const statement of caseNode.consequent) {
                (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.statement(statement);
            }
        }
        context.breakTargets.pop();
        context.breakEnvDepths.pop();
        return breakJumpIndex;
    }
    scanSwitchCasesForLexicalDeclarations(cases) {
        const lexicalVars = new Set();
        for (const caseNode of cases) {
            for (const stmt of caseNode.consequent) {
                if (NodeGuards_1.NodeGuards.isVariableDeclarationNode(stmt)) {
                    if (stmt.kind === 'let' || stmt.kind === 'const') {
                        for (const decl of stmt.declarations) {
                            if (NodeGuards_1.NodeGuards.isIdentifierNode(decl.id)) {
                                lexicalVars.add(decl.id.name);
                            }
                            else if (NodeGuards_1.NodeGuards.isArrayPatternNode(decl.id) ||
                                NodeGuards_1.NodeGuards.isObjectPatternNode(decl.id)) {
                                for (const id of this.vmCompilerUtils.extractIdentifiersFromPattern(decl.id)) {
                                    lexicalVars.add(id);
                                }
                            }
                        }
                    }
                }
            }
        }
        return lexicalVars;
    }
    patchSwitchJumpTargets(caseLabelTargets, caseBodyTargets, defaultCaseIndex, defaultJumpIndex, breakJumpIndex, endTarget, context) {
        for (let i = 0; i < caseLabelTargets.length; i++) {
            const jumpInstrIndex = caseLabelTargets[i];
            let targetCaseIndex = i;
            if (defaultCaseIndex !== -1 && i >= defaultCaseIndex) {
                targetCaseIndex++;
            }
            context.instructions[jumpInstrIndex].target = caseBodyTargets[targetCaseIndex];
        }
        if (defaultCaseIndex !== -1) {
            context.instructions[defaultJumpIndex].target = caseBodyTargets[defaultCaseIndex];
        }
        else {
            context.instructions[defaultJumpIndex].target = endTarget;
        }
        this.vmCompilerUtils.patchJumpTargets(breakJumpIndex, endTarget, breakJumpIndex, endTarget, context);
    }
};
exports.VMConditionalCompiler = VMConditionalCompiler;
__decorate([
    (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IVMCompilerUtils),
    __metadata("design:type", typeof (_a = typeof IVMCompilerUtils_1.IVMCompilerUtils !== "undefined" && IVMCompilerUtils_1.IVMCompilerUtils) === "function" ? _a : Object)
], VMConditionalCompiler.prototype, "vmCompilerUtils", void 0);
__decorate([
    (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IVMScopeManager),
    __metadata("design:type", typeof (_b = typeof IVMScopeManager_1.IVMScopeManager !== "undefined" && IVMScopeManager_1.IVMScopeManager) === "function" ? _b : Object)
], VMConditionalCompiler.prototype, "scopeManager", void 0);
exports.VMConditionalCompiler = VMConditionalCompiler = __decorate([
    (0, inversify_1.injectable)()
], VMConditionalCompiler);
